import { Card } from '@mui/material';
import { GRID_DETAIL_PANEL_TOGGLE_FIELD, useGridApiRef } from '@mui/x-data-grid-premium';
import { RiAlertFill, RiLinkUnlinkM } from '@remixicon/react';
import { useMemo } from 'react';
import { parseQboAccountMetadata } from 'shared/mappers/ThirdPartyEntities';
import { AgButton, AgText } from 'src/components/ui';
import { DataGrid } from 'src/modules/grids';
import { LedgerAccountDetail } from 'types/graphql';
import { useThirdAndFirstPartyEntities } from './quickbooks.api';
import useQBOAccountMappingColumns, { AccountLinkDetailPanel, Row } from './useQBOAccountMappingColumns';

type Props = { onReconnect: () => void; canConnect: boolean };

export default function AccountMappingsTable({ onReconnect, canConnect }: Props) {
  const apiRef = useGridApiRef();
  const { data: entitiesData, loading } = useThirdAndFirstPartyEntities();

  type EntityLinkRow = {
    id: number;
    metadata?: unknown;
    account?: {
      id: number;
      fragmentAccountPath?: string | null;
      accountNumber?: string | null;
      accountName: string;
    } | null;
  };

  const rows: Row[] = useMemo(
    () =>
      entitiesData?.thirdPartyIntegrationEntityLinks?.entities?.map((e: EntityLinkRow) => {
        const config = parseQboAccountMetadata((e.metadata ?? {}) as Parameters<typeof parseQboAccountMetadata>[0]);

        return {
          id: e.id,
          entityLink: { id: e.id },
          quickBooks: config,
          agriful: e.account
            ? {
                id: e.account.id,
                path: e.account.fragmentAccountPath,
                number: e.account.accountNumber,
                name: e.account.accountName,
              }
            : null,
        };
      }) ?? [],
    [entitiesData]
  );

  const columns = useQBOAccountMappingColumns();
  const availableAccounts = entitiesData?.ledgerAccountDetails;

  // Map of Agriful ledger account id -> the QBO account label it's already
  // linked to. Used by the detail panel to detect override conflicts.
  const existingAgrifulLinks = useMemo(() => {
    const map = new Map<number, string>();
    rows.forEach((r) => {
      if (r.agriful?.id) {
        const qboLabel = [r.quickBooks?.accountNumber, r.quickBooks?.name].filter(Boolean).join(' ');
        map.set(r.agriful.id, qboLabel || 'another QBO account');
      }
    });
    return map;
  }, [rows]);

  const hasUnmapped = useMemo(() => rows.some((r) => !r.agriful), [rows]);

  if (!loading && rows.length === 0) {
    return <AccountsUnavailableState onReconnect={onReconnect} canConnect={canConnect} />;
  }

  return (
    <div className="w-full">
      {hasUnmapped && (
        <div className="mb-4 flex gap-3 rounded-[12px] border border-orange-border-light bg-orange-surface p-4 text-orange-status">
          <RiAlertFill size={20} className="shrink-0 text-orange-icon" />
          <div className="flex flex-col gap-1">
            <AgText size="14" weight="semibold" color="inherit">
              Unmapped accounts
            </AgText>
            <AgText size="14" color="inherit">
              There are some accounts that were not mapped automatically. Please select corresponding accounts.
            </AgText>
          </div>
        </div>
      )}
      <DataGrid
        rows={rows}
        loading={loading}
        columns={columns}
        gridKey="settings.accounting.qbo-account-mappings"
        storageKey="account-mapping"
        apiRef={apiRef}
        pagination
        noBorder
        sx={{
          '& .MuiDataGrid-columnHeader:first-of-type, & .MuiDataGrid-cell:first-of-type': { paddingLeft: 0 },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#0F132499',
            fontFamily: 'var(--typography-family-inter, Inter, sans-serif)',
            fontWeight: 400,
            fontSize: 'var(--typography-size-caption-l, 14px)',
            lineHeight: 'var(--typography-line-height-caption-l, 20px)',
            letterSpacing: 'var(--typography-letter-spacing-caption-l, 0)',
          },
          '& .MuiDataGrid-cell[data-field="quickBooks"]': { color: '#14151A' },
          '& .MuiDataGrid-cell[data-field="agriful"]': {
            fontFamily: 'var(--typography-family-inter, Inter, sans-serif)',
            fontWeight: 400,
            fontSize: 'var(--typography-size-caption-l, 14px)',
            lineHeight: 'var(--typography-line-height-caption-l, 20px)',
            letterSpacing: 'var(--typography-letter-spacing-caption-l, 0)',
          },
          '& .MuiDataGrid-row.row-unmapped .MuiDataGrid-cell[data-field="quickBooks"]': {
            background: 'linear-gradient(var(--color-orange-icon) 0 0) left center / 2px 60% no-repeat',
            paddingLeft: '10px',
          },
        }}
        getRowClassName={(params) => (params.row.agriful ? '' : 'row-unmapped')}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        columnVisibilityModel={{ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }}
        getDetailPanelHeight={() => 'auto' as const}
        getDetailPanelContent={({ row }) => (
          <AccountLinkDetailPanel
            row={row as Row}
            availableAccounts={(availableAccounts ?? []) as LedgerAccountDetail[]}
            existingAgrifulLinks={existingAgrifulLinks}
            onLinked={() => {}}
          />
        )}
      />
    </div>
  );
}

function AccountsUnavailableState({ onReconnect, canConnect }: Props) {
  return (
    <Card sx={{ width: '100%' }}>
      <div className="flex flex-col items-center gap-6 px-6 py-12">
        <RiLinkUnlinkM size={44} className="text-neutral-alpha-9" />

        <div className="flex max-w-[620px] flex-col items-center gap-2 text-center">
          <AgText size="16" weight="semibold">
            QuickBooks accounts couldn't be loaded
          </AgText>
          <AgText size="14" className="text-neutral-alpha-9">
            Your QuickBooks connection is active, but we weren't able to retrieve your chart of accounts. This can
            happen when permissions change or the connection needs to be refreshed. Re-connecting takes about 30 seconds
            and won't affect your existing sync history.
          </AgText>
        </div>

        <AgButton variant="primary" onPress={onReconnect} isDisabled={!canConnect}>
          Reconnect to QuickBooks
        </AgButton>

        <div className="flex items-center gap-1">
          <AgText size="14" className="text-neutral-alpha-9">
            Having troubles?
          </AgText>
          <a href="mailto:support@agrifulsoftware.com" className="text-14 font-medium text-brand-text hover:underline">
            Contact support
          </a>
        </div>
      </div>
    </Card>
  );
}
